import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, forkJoin, map, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { baseUrl } from '../Data/service/global.service';
import {
  DashboardExpenseBucketItem,
  DashboardExpensesByType,
  DashboardFinancialPeriod,
  DashboardFinancialSummary,
} from './invoice-subscriptions-v2.service';

interface ExpenseReportCategory {
  category_id: number;
  category_name_ar?: string;
  category_name_en?: string;
  category_name_he?: string;
  total_amount: number;
  percentage: number;
}

interface ExpenseReportData {
  total_income: number;
  total_expenses: number;
  net_amount: number;
  expenses_by_category: ExpenseReportCategory[];
}

interface ExpenseReportResponse {
  success: boolean;
  data: ExpenseReportData;
  company_id: number;
  start_date: string;
  end_date: string;
}

const FALLBACK_CATEGORY_LABELS = {
  label_ar: 'أخرى',
  label_en: 'Other',
  label_he: 'אחר',
};

function fmtYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function calcGrowth(current: number, previous: number): number | null {
  if (previous === 0) return null;
  return Number((((current - previous) / previous) * 100).toFixed(1));
}

function toPeriod(start: Date, end: Date, data: ExpenseReportData, comparison: number | null): DashboardFinancialPeriod {
  return {
    start_date: fmtYmd(start),
    end_date: fmtYmd(end),
    income: data.total_income,
    expenses: data.total_expenses,
    profit: data.net_amount,
    comparison_percent: comparison,
  };
}

/** One chart slice per expense sub-category returned by GET /expenses/reports. */
function mapExpenseCategories(categories: ExpenseReportCategory[]): DashboardExpenseBucketItem[] {
  const items = (categories ?? [])
    .map((cat) => {
      const amount = Number(cat.total_amount ?? 0);
      const categoryId = cat.category_id != null ? Number(cat.category_id) : null;
      const hasId = categoryId != null && Number.isFinite(categoryId) && categoryId > 0;
      const key: DashboardExpenseBucketItem['key'] = hasId ? `cat_${categoryId}` : 'other';
      return {
        key,
        label_ar: cat.category_name_ar || cat.category_name_en || FALLBACK_CATEGORY_LABELS.label_ar,
        label_en: cat.category_name_en || cat.category_name_ar || FALLBACK_CATEGORY_LABELS.label_en,
        label_he: cat.category_name_he || cat.category_name_en || FALLBACK_CATEGORY_LABELS.label_he,
        amount: Number(amount.toFixed(2)),
        percentage: 0,
        category_ids: hasId ? [categoryId!] : [],
      };
    })
    .filter((item) => item.amount > 0);

  const grand = items.reduce((sum, item) => sum + item.amount, 0);
  return items
    .map((item) => ({
      ...item,
      percentage:
        grand > 0
          ? Number(((item.amount / grand) * 100).toFixed(1))
          : 0,
    }))
    .sort((a, b) => b.amount - a.amount);
}

@Injectable({ providedIn: 'root' })
export class DashboardFinancialService {
  constructor(private http: HttpClient) {}

  private fetchReport(companyId: number, start: string, end: string): Observable<ExpenseReportData> {
    const params = new HttpParams()
      .set('company_id', String(companyId))
      .set('start_date', start)
      .set('end_date', end)
      .set('confirmation_status', 'all')
      .set('date_type', 'document_date');
    return this.http.get<ExpenseReportResponse>(`${baseUrl}expenses/reports`, { params }).pipe(
      map((res) => res.data),
    );
  }

  /**
   * Income totals for the last 6 calendar months, oldest first.
   * Current month is start-of-month → today, matching the financial summary row.
   * Earlier months are full calendar months. Missing months resolve to 0.
   */
  loadIncomeLast6Months(companyId: number): Observable<number[]> {
    const now = new Date();
    const requests: Observable<number>[] = [];
    for (let i = 5; i >= 0; i--) {
      const start = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const end = i === 0 ? new Date(now) : new Date(now.getFullYear(), now.getMonth() - i + 1, 0);
      requests.push(
        this.fetchReport(companyId, fmtYmd(start), fmtYmd(end)).pipe(
          map((data) => Number(data?.total_income ?? 0)),
          catchError(() => of(0)),
        ),
      );
    }
    return forkJoin(requests);
  }

  /** Expense buckets for the dashboard donut / legend. */
  loadExpensesByType(
    companyId: number,
    period: 'current_month' | 'previous_month' = 'current_month',
  ): Observable<DashboardExpensesByType> {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfNow = new Date(now);
    const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);

    const range =
      period === 'previous_month'
        ? { start: startOfLastMonth, end: endOfLastMonth, key: 'previous_month' as const }
        : { start: startOfMonth, end: endOfNow, key: 'current_month' as const };

    return this.fetchReport(companyId, fmtYmd(range.start), fmtYmd(range.end)).pipe(
      map((current) => ({
        period: range.key,
        start_date: fmtYmd(range.start),
        end_date: fmtYmd(range.end),
        total_expenses: current.total_expenses,
        items: mapExpenseCategories(current.expenses_by_category),
      })),
    );
  }

  loadFinancialBlocks(companyId: number): Observable<{
    financial_summary: DashboardFinancialSummary;
    expenses_by_type: DashboardExpensesByType;
  }> {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfNow = new Date(now);
    const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
    const startOfPrevPrev = new Date(now.getFullYear(), now.getMonth() - 2, 1);
    const endOfPrevPrev = new Date(now.getFullYear(), now.getMonth() - 1, 0);
    const startOfYear = new Date(now.getFullYear(), 0, 1);
    const startOfLastYearYtd = new Date(now.getFullYear() - 1, 0, 1);
    const endOfLastYearYtd = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());

    return forkJoin({
      current: this.fetchReport(companyId, fmtYmd(startOfMonth), fmtYmd(endOfNow)),
      previous: this.fetchReport(companyId, fmtYmd(startOfLastMonth), fmtYmd(endOfLastMonth)),
      ytd: this.fetchReport(companyId, fmtYmd(startOfYear), fmtYmd(endOfNow)),
      prevPrev: this.fetchReport(companyId, fmtYmd(startOfPrevPrev), fmtYmd(endOfPrevPrev)),
      lastYearYtd: this.fetchReport(companyId, fmtYmd(startOfLastYearYtd), fmtYmd(endOfLastYearYtd)),
    }).pipe(
      map(({ current, previous, ytd, prevPrev, lastYearYtd }) => ({
        financial_summary: {
          company_id: companyId,
          current_month: toPeriod(startOfMonth, endOfNow, current, calcGrowth(current.net_amount, previous.net_amount)),
          previous_month: toPeriod(
            startOfLastMonth,
            endOfLastMonth,
            previous,
            calcGrowth(previous.net_amount, prevPrev.net_amount),
          ),
          year_to_date: toPeriod(startOfYear, endOfNow, ytd, calcGrowth(ytd.net_amount, lastYearYtd.net_amount)),
        },
        expenses_by_type: {
          period: 'current_month',
          start_date: fmtYmd(startOfMonth),
          end_date: fmtYmd(endOfNow),
          total_expenses: current.total_expenses,
          items: mapExpenseCategories(current.expenses_by_category),
        },
      })),
    );
  }

  resolveCompanyId(): number | null {
    const raw = localStorage.getItem('companyDefault');
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      return typeof parsed === 'number' ? parsed : Number(parsed);
    } catch {
      const n = Number(raw);
      return Number.isFinite(n) ? n : null;
    }
  }
}
